import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { AlertBar, AlertMessageKind } from "client/Components";
import { AlertBarAnimator } from "./AlertBarAnimator";
import {
	Alert,
	AlertManager
} from "client/Hooks";
import styles from "./AlertBarContainer.module.less";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";

type AlertEntity = {
	id: number;
	kind: AlertMessageKind;
	content: string | ReactElement;
	isBeingRemoved: boolean;
};

export type AlertContainerProps = {
	alerts: readonly Alert[];
	alertManager: AlertManager;
	trackingId: string;
};

export const AlertBarContainer = (props: AlertContainerProps): ReactElement => {

	const {
		alerts,
		alertManager,
		trackingId
	} = props;

	const [entities, setEntities] = useState<AlertEntity[]>([]);

	const addHandler = useCallback(
		(alert: Alert): void => {
			const {
				id,
				kind,
				content
			} = alert;

			const entity: AlertEntity = {
				id,
				kind,
				content,
				isBeingRemoved: false
			};

			setEntities((x) => [...x, entity]);
		},
		[]
	);

	const removeHandler = useCallback(
		(id: number): void => {
			setEntities(
				(x) => {
					const copy: AlertEntity[] = [...x];

					const index: number = copy.findIndex(x => x.id === id);
					if (index !== -1) {
						const entity: AlertEntity = {
							...copy[index],
							isBeingRemoved: true
						};
						copy.splice(index, 1, entity);
					}

					return copy;
				}
			);
		},
		[]
	);

	const removedHandler = (id: number): void => {
		setEntities((x) => x.filter(x => x.id !== id));
	};

	useEffect(
		() => {
			for (const alert of entities) {
				if (alert.isBeingRemoved) {
					continue;
				}
				if (!alerts.some(x => x.id === alert.id)) {
					removeHandler(alert.id);
				}
			}

			const addedItems: readonly Alert[] = alerts.filter(x => !entities.some(z => z.id === x.id));
			for (const added of addedItems) {
				addHandler(added);
			}
		},
		[
			alerts,
			entities,
			addHandler,
			removeHandler
		]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			{entities.map(x => (
				<AlertBarAnimator
					key={x.id}
					isBeingRemoved={x.isBeingRemoved}
					onRemoved={() => removedHandler(x.id)}
				>
					<AlertBar
						kind={x.kind}
						onDismiss={() => alertManager.remove(x.id)}
						trackingId={generateTrackingId(`alert`, `|${x.id}`)}
					>
						{x.content}
					</AlertBar>
				</AlertBarAnimator>
			))}
		</div>
	);
};
AlertBarContainer.displayName = "AlertBarContainer";
